import { useSyncExternalStore } from "react";
import type { AuthResult, Me, MfaStatus, Organization, Project } from "@backstory/protocol";
import { api, ApiRequestError, DEV_TOKEN_AUTH, clearCsrf, devAuthSuppressed, MFA_REQUIRED_EVENT, rememberCsrf, setApiEnvironment, setApiOrg, setApiProject, setDevAuthSuppressed, UNAUTHORIZED_EVENT } from "../api";

export type AuthStatus = "unknown" | "anon" | "authed";

export interface AuthState {
  status: AuthStatus;
  me?: Me;
  currentOrgId?: string;
  currentProjectId?: string;
  /** Global analytical scope. Undefined means all environments. */
  currentEnvironment?: string;
  /** Project list for the current org (loaded lazily by the shell). */
  projects: Array<{ id: string; name: string }>;
  /** Pending step from the last auth result. */
  next?: AuthResult["next"];
  /** True when the instance has never been through first-run setup. */
  setupRequired?: boolean;
  error?: string;
}

const LS_ORG = "backstory-org";
const LS_PROJECT = "backstory-project";
const LS_ENVIRONMENTS = "backstory-environments";

let state: AuthState = { status: "unknown", projects: [] };
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}
function set(patch: Partial<AuthState>) {
  state = { ...state, ...patch };
  if ("currentProjectId" in patch) setApiProject(patch.currentProjectId);
  if ("currentEnvironment" in patch) setApiEnvironment(patch.currentEnvironment);
  emit();
}

function read(key: string): string | undefined {
  try {
    return localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}
function write(key: string, v: string | undefined) {
  try {
    if (v) localStorage.setItem(key, v);
    else localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function environmentFor(projectId: string | undefined): string | undefined {
  if (!projectId) return undefined;
  try {
    const saved = JSON.parse(localStorage.getItem(LS_ENVIRONMENTS) ?? "{}") as Record<string, string>;
    return saved[projectId] || undefined;
  } catch {
    return undefined;
  }
}

function rememberEnvironment(projectId: string | undefined, environment: string | undefined) {
  if (!projectId) return;
  try {
    const saved = JSON.parse(localStorage.getItem(LS_ENVIRONMENTS) ?? "{}") as Record<string, string>;
    if (environment) saved[projectId] = environment;
    else delete saved[projectId];
    localStorage.setItem(LS_ENVIRONMENTS, JSON.stringify(saved));
  } catch {
    /* ignore */
  }
}

export const authStore = {
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  get: () => state,

  /** Resolve the current principal. Cookie session first; dev-token synthesis as a fallback. */
  async load(): Promise<AuthState> {
    try {
      const me = await api.me.get(true);
      return authStore.applyMe(me);
    } catch (e) {
      if (DEV_TOKEN_AUTH && !devAuthSuppressed()) {
        // No auth backend or no cookie: synthesize a local principal from the dev project.
        try {
          const p = await api.project();
          return authStore.applyMe(devMe(p));
        } catch {
          /* fall through */
        }
      }
      const status = e instanceof ApiRequestError && e.code === "network" ? "unknown" : "anon";
      set({ status, me: undefined, error: e instanceof ApiRequestError && e.code === "network" ? e.message : undefined });
      if (status === "anon") await authStore.probeSetup();
      return state;
    }
  },

  /** Ask whether this instance still needs first-run setup. Never throws. */
  async probeSetup(): Promise<boolean> {
    try {
      const { setupRequired } = await api.setup.status();
      set({ setupRequired });
      return setupRequired;
    } catch {
      return false;
    }
  },

  applyMe(me: Me): AuthState {
    const orgIds = me.orgs.map((o) => o.id);
    const remembered = read(LS_ORG);
    const currentOrgId = remembered && orgIds.includes(remembered) ? remembered : orgIds[0];
    const project = read(LS_PROJECT);
    setDevAuthSuppressed(false);
    rememberCsrf((me as { csrfToken?: string }).csrfToken);
    // /v1/me is the authority on enrollment, so a login-time setup_mfa hint is
    // stale once it reports a factor. Releasing the gate here (rather than in the
    // enrollment screen) means the hint can only ever be dropped by real state.
    const next = state.next === "setup_mfa" && !mfaSetupPending(me.mfa) ? undefined : state.next;
    set({ status: "authed", me, currentOrgId, currentProjectId: project, currentEnvironment: environmentFor(project), setupRequired: false, error: undefined, next });
    return state;
  },

  applyAuthResult(r: AuthResult): AuthState {
    // A login result carries no MFA detail, only the `next` hint. When it says
    // setup_mfa the server has just told us this user owes enrollment, so record
    // that rather than an optimistic placeholder: assuming "not required" is what
    // let the dashboard render for a moment before /v1/me arrived.
    const mfa: MfaStatus =
      r.next === "setup_mfa"
        ? { totpEnabled: false, passkeys: [], recoveryCodesRemaining: 0, required: true }
        : (state.me?.mfa ?? { totpEnabled: false, passkeys: [], recoveryCodesRemaining: 0, required: false });
    set({ next: r.next });
    rememberCsrf(r.csrfToken);
    authStore.applyMe({ user: r.user, orgs: r.orgs, mfa });
    return state;
  },

  setOrg(orgId: string) {
    write(LS_ORG, orgId);
    write(LS_PROJECT, undefined);
    set({ currentOrgId: orgId, currentProjectId: undefined, currentEnvironment: undefined, projects: [] });
  },
  setProjects(projects: AuthState["projects"]) {
    const remembered = read(LS_PROJECT);
    const current = remembered && projects.some((p) => p.id === remembered) ? remembered : projects[0]?.id;
    write(LS_PROJECT, current);
    set({ projects, currentProjectId: current, currentEnvironment: environmentFor(current) });
  },
  setProject(projectId: string) {
    write(LS_PROJECT, projectId);
    set({ currentProjectId: projectId, currentEnvironment: environmentFor(projectId) });
  },
  setEnvironment(environment: string | undefined) {
    const value = environment || undefined;
    rememberEnvironment(state.currentProjectId, value);
    set({ currentEnvironment: value });
  },
  /** Clear the pending step. Pass the step you mean to finish so one flow cannot drop another's. */
  clearNext(only?: AuthResult["next"]) {
    if (only && state.next !== only) return;
    set({ next: undefined });
  },

  async logout() {
    try {
      await api.auth.logout();
    } catch {
      /* ignore */
    }
    setDevAuthSuppressed(true);
    clearCsrf();
    write(LS_ORG, undefined);
    write(LS_PROJECT, undefined);
    set({ status: "anon", me: undefined, currentOrgId: undefined, currentProjectId: undefined, currentEnvironment: undefined, projects: [], next: undefined });
  },

  /** Called by the global 401 listener. */
  markAnon() {
    if (state.status !== "anon") set({ status: "anon", me: undefined });
  },

  /** Called by the global 403 mfa_required listener: the API refuses product data. */
  markMfaRequired() {
    if (state.status === "authed" && state.next !== "setup_mfa") set({ next: "setup_mfa" });
  },

  /** Test helper. */
  _reset() {
    state = { status: "unknown", projects: [] };
    clearCsrf();
    setDevAuthSuppressed(false);
    setApiProject(undefined);
    setApiEnvironment(undefined);
    setApiOrg(undefined);
    emit();
  },
};

function devMe(p: Project): Me {
  const org: Organization = {
    id: p.tenantId,
    slug: "local",
    name: "Local",
    plan: "team",
    role: "owner",
    settings: { requireSso: false, breakGlassUserIds: [], sessionIdleMinutes: 720, sessionAbsoluteHours: 168, phi: false, dataRegion: "us", ipAllowlistEnabled: false, ipAllowlist: [], ipBypassUserIds: [] },
    profile: {},
    createdAt: new Date(0).toISOString(),
  };
  return {
    // Locally one person owns both the workspace and the platform. The API
    // treats this token the same way, so the staff console (including who else
    // may run it) has to be reachable here too.
    user: { id: "dev", email: "dev@localhost", emailVerified: true, name: "Local developer", createdAt: new Date(0).toISOString(), staff: true, staffRole: "owner" },
    orgs: [org],
    mfa: { totpEnabled: false, passkeys: [], recoveryCodesRemaining: 0, required: false },
  };
}

let loginNotice: string | undefined;

/** A message to show on the sign-in page after the session was ended. Read once. */
export function consumeLoginNotice(): string | undefined {
  const message = loginNotice;
  loginNotice = undefined;
  return message;
}

if (typeof window !== "undefined") {
  window.addEventListener(UNAUTHORIZED_EVENT, (ev) => {
    const detail = (ev as CustomEvent<{ code?: string; message?: string }>).detail;
    if (detail?.code === "domain_claimed" && detail.message) loginNotice = detail.message;
    if (!DEV_TOKEN_AUTH || devAuthSuppressed()) authStore.markAnon();
  });
  window.addEventListener(MFA_REQUIRED_EVENT, () => authStore.markMfaRequired());
}

/** True when this principal owes MFA enrollment: the org requires it and no factor exists. */
export function mfaSetupPending(mfa: MfaStatus | undefined): boolean {
  return !!mfa && mfa.required && !mfa.totpEnabled && mfa.passkeys.length === 0;
}

export function useAuth(): AuthState {
  return useSyncExternalStore(authStore.subscribe, authStore.get, authStore.get);
}

export function currentOrg(s: AuthState): Organization | undefined {
  return s.me?.orgs.find((o) => o.id === s.currentOrgId);
}

export function hasRole(s: AuthState, ...roles: Array<Organization["role"]>): boolean {
  const o = currentOrg(s);
  return !!o?.role && roles.includes(o.role);
}
